import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaPlus, FaEdit, FaTrash, FaEye, FaSave, FaSearch,
  FaFlask, FaCapsules, FaFileAlt, FaDownload, FaRobot, FaUpload, FaCheck, FaUnlink,
  FaHourglassHalf, FaExclamationTriangle, FaRadiation, FaFire, FaCalculator, FaTimes
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { apiMasterAPI, settingsAPI, aiAPI, commercialConfigAPI, integrationsAPI } from '../services/api';
import { withReportProfile, unitReportAddress } from '../utils/reportProfiles';
import useApprovalRights from '../hooks/useApprovalRights';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import { Modal } from '../components/Modals';
import VersionHistoryModal from '../components/VersionHistoryModal';
import { toDataUrlIfPossible } from '../utils/printUtils';
import { useAppSettings } from '../context/AppSettingsContext';
import useAIAction from '../hooks/useAIAction';
import AIResultPanel from '../components/AIResultPanel';
import { ActionsMenu } from '../components/ActionsMenu';
import { StatCard } from '../components/UnifiedUIComponents';
import TablePagination from '../components/TablePagination';
import CleanabilityAssessmentModal from '../components/CleanabilityAssessmentModal';
import ChangeReferenceModal from '../components/ChangeReferenceModal';
import { DEFAULT_CLEANABILITY_CONFIG, cleanabilityAssessmentReportHTML, exportCleanabilityChecklistXlsx } from '../utils/cleanabilityAssessment';
import { printAssessmentReport } from '../utils/assessmentReports';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const toFileUrl = (file) => {
  if (!file) return '';
  if (typeof file === 'string' && /^https?:\/\//i.test(file)) return file;
  return `${API_ORIGIN}/uploads/${file}`;
};

const SOLUBILITY_OPTIONS = [
  'Very Soluble', 'Freely Soluble', 'Soluble', 'Sparingly Soluble',
  'Slightly Soluble', 'Very Slightly Soluble', 'Practically Insoluble', 'Insoluble'
];
const CLEANABILITY_OPTIONS = ['Easy', 'Medium', 'Difficult'];
const DOSE_UNIT_OPTIONS = ['mg', 'g', 'mcg', 'IU', 'MIU'];
const PDE_UNIT_OPTIONS = ['mg', 'mcg', 'mg/day', 'mcg/day', 'IU', 'MIU'];
const LD50_UNIT_OPTIONS = ['mg/kg', 'mcg/kg', 'g/kg', 'mg', 'mcg', 'IU', 'MIU'];

const STATUS_STYLES = {
  'Approved': { background: 'var(--success-bg)', color: 'var(--success-text)' },
  'Pending Product Approval': { background: 'var(--warning-bg)', color: 'var(--warning-text)' },
  'Pending QA Approval': { background: 'var(--info-bg)', color: 'var(--info-text)' },
  'Returned': { background: 'var(--warning-bg)', color: 'var(--warning-text)' },
  'Edit Required': { background: 'var(--warning-bg)', color: 'var(--warning-text)' },
  'Rejected': { background: 'var(--danger-bg)', color: 'var(--danger-text)' },
};
const LOCKED_STATUSES = ['Pending Product Approval', 'Pending QA Approval'];
const STATUS_FILTER_OPTIONS = ['All', 'Approved', 'Pending Product Approval', 'Pending QA Approval', 'Returned', 'Edit Required', 'Rejected'];
// Group filters driven by the summary cards (also selectable from the status dropdown).
const ANY_PENDING = 'Any pending';
const NEEDS_ATTENTION = 'Needs attention';
const STATUS_GROUPS = {
  [ANY_PENDING]: ['Pending Product Approval', 'Pending QA Approval'],
  [NEEDS_ATTENTION]: ['Returned', 'Edit Required', 'Rejected'],
};


// Import column header (as in the Excel export / template) -> API field
const IMPORT_HEADER_MAP = {
  'api code': 'api_code', 'api name': 'api_name', 'production line': 'production_line',
  'max daily dose': 'max_daily_dose', 'max daily dose unit': 'max_daily_dose_unit',
  'therapeutic dose': 'therapeutic_dose', 'therapeutic dose unit': 'therapeutic_dose_unit',
  'pde value': 'pde_value', 'pde unit': 'pde_unit', 'ld50 value': 'ld50_value', 'ld50 unit': 'ld50_unit',
  'solubility classification': 'solubility_classification', 'cleanability rating': 'cleanability_rating',
  'genotoxic': 'is_genotoxic', 'carcinogenic': 'is_carcinogenic', 'sensitizer': 'is_sensitizer', 'notes': 'notes',
};
const IMPORT_TEMPLATE_ROW = {
  'API Code': 'API-001', 'API Name': 'Paracetamol', 'Production Line': '',
  'Max Daily Dose': 4000, 'Max Daily Dose Unit': 'mg', 'Therapeutic Dose': 500, 'Therapeutic Dose Unit': 'mg',
  'PDE Value': '', 'PDE Unit': 'mg', 'LD50 Value': 1944, 'LD50 Unit': 'mg/kg',
  'Solubility Classification': 'Sparingly Soluble', 'Cleanability Rating': 'Easy',
  'Genotoxic': 'No', 'Carcinogenic': 'No', 'Sensitizer': 'No', 'Notes': '',
};

const EMPTY_FORM = {
  api_code: '', api_name: '',
  max_daily_dose: '', max_daily_dose_unit: 'mg',
  therapeutic_dose: '', therapeutic_dose_unit: 'mg',
  pde_value: '', pde_unit: 'mg',
  ld50_value: '', ld50_unit: 'mg/kg',
  solubility_classification: '', cleanability_rating: '',
  is_genotoxic: false, is_carcinogenic: false, is_sensitizer: false,
  notes: '', production_line: '',
};

const HAZARD_BADGE_STYLE = { background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '2px 8px', borderRadius: '999px', fontSize: 'var(--fs-xs)', fontWeight: 700, marginRight: '4px', whiteSpace: 'nowrap' };

const RISK_LEVEL_COLORS = Object.freeze({
  green:  { badge: '#dcfce7', text: '#15803d', border: '#86efac' },
  yellow: { badge: '#fef9c3', text: '#a16207', border: '#fde047' },
  orange: { badge: '#ffedd5', text: '#c2410c', border: '#fdba74' },
  red:    { badge: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
});

// Mirrors backend/services/riskAssessmentEngine.js scoring maps, limited to the
// factors known purely from API master data (toxicity, solubility, cleanability).
// Patient population / batch size / equipment sharing are product-specific and
// are only available once the API is used on a product — see Risk Assessment.
const scoreFromToxicity = (pdeValue, ld50Value) => {
  const pde = Number(pdeValue);
  if (pdeValue !== null && pdeValue !== undefined && pdeValue !== '' && !Number.isNaN(pde) && pde > 0) {
    if (pde > 1000) return 1;
    if (pde >= 100) return 2;
    return 3;
  }
  const ld50 = Number(ld50Value);
  if (ld50Value !== null && ld50Value !== undefined && ld50Value !== '' && !Number.isNaN(ld50) && ld50 > 0) {
    if (ld50 > 2000) return 1;
    if (ld50 >= 500) return 2;
    return 3;
  }
  return 2;
};
const SOLUBILITY_SCORE_MAP = {
  'very soluble': 1, 'freely soluble': 1,
  'soluble': 2, 'sparingly soluble': 2,
  'slightly soluble': 3, 'very slightly soluble': 3, 'practically insoluble': 3, 'insoluble': 3,
};
const scoreFromSolubility = (classification) => SOLUBILITY_SCORE_MAP[String(classification || '').trim().toLowerCase()] ?? 2;
const CLEANABILITY_SCORE_MAP = { easy: 1, medium: 2, difficult: 3 };
const scoreFromCleanability = (rating) => CLEANABILITY_SCORE_MAP[String(rating || '').trim().toLowerCase()] ?? 2;

// 3-9 range across the three API-level sub-scores; escalated one tier for genotoxic/carcinogenic APIs.
const getApiRiskFactor = (api) => {
  const toxicity = scoreFromToxicity(api.pde_value, api.ld50_value);
  const solubility = scoreFromSolubility(api.solubility_classification);
  const cleanability = scoreFromCleanability(api.cleanability_rating);
  let total = toxicity + solubility + cleanability;
  const escalated = api.is_genotoxic || api.is_carcinogenic;
  if (escalated) total += 2;

  let risk_level, risk_color;
  if (total >= 9) { risk_level = 'High'; risk_color = 'red'; }
  else if (total >= 7) { risk_level = 'Medium'; risk_color = 'orange'; }
  else if (total >= 5) { risk_level = 'Low-Medium'; risk_color = 'yellow'; }
  else { risk_level = 'Low'; risk_color = 'green'; }

  return { total, risk_level, risk_color };
};

const ApiMaster = () => {
  const { appName } = useAppSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const ai = useAIAction();
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [viewOnly, setViewOnly] = useState(false);
  const initialFormRef = useRef('');
  const [editingId, setEditingId] = useState(null);
  const [historyModal, setHistoryModal] = useState(null); // api row
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [files, setFiles] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [hazardOnly, setHazardOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [highRiskOnly, setHighRiskOnly] = useState(false);
  const [editingApi, setEditingApi] = useState(null);
  const [qmsLinked, setQmsLinked] = useState(false);
  // { api, readOnly } while the Change Control/Deviation/CAPA gate is open for editing an Approved API
  const [changeRefGate, setChangeRefGate] = useState(null);
  const [changeRefBusy, setChangeRefBusy] = useState(false);
  // Reference captured for the API currently being edited — sent along with the save
  const [pendingChangeRef, setPendingChangeRef] = useState(null);
  // Pending e-signature: { title, description, decisionOptions?, confirmLabel, run(signature) }
  const [signRequest, setSignRequest] = useState(null);
  const [signForm, setSignForm] = useState({ password: '', comment: '', decision: 'approve' });
  const [signBusy, setSignBusy] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { fileName, rows }
  const [importResult, setImportResult] = useState(null);
  const importInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [cleanConfig, setCleanConfig] = useState(DEFAULT_CLEANABILITY_CONFIG);
  // null | { readOnly } - the "Help me rate" scoring helper dialog
  const [assessmentDialog, setAssessmentDialog] = useState(null);
  const [reportSettings, setReportSettings] = useState({
    report_client_name: 'Client Name',
    report_client_address: 'Client Address',
    report_document_name: 'API Master Report',
    report_document_rev_no: '01',
    report_client_logo: '',
    report_primeclean_logo: '',
    report_footer_tagline: `Data is generated and handled by ${appName}.`,
    report_format_no_api_master: ''
  });

  let currentUserRole = '';
  try {
    currentUserRole = JSON.parse(localStorage.getItem('user') || '{}')?.role || '';
  } catch (error) {
    currentUserRole = '';
  }
  const canManage = ['QA', 'Admin', 'Super Admin'].includes(currentUserRole);

  // SAP / other-app API sync (optional, Integration Hub).
  const [apiSyncSources, setApiSyncSources] = useState([]);
  const [fetchingSource, setFetchingSource] = useState('');
  useEffect(() => {
    commercialConfigAPI.getRuntime().then((res) => {
      const integrations = res?.data?.data?.integrations || {};
      setApiSyncSources(Object.entries(integrations).filter(([, item]) => item?.features?.api_sync).map(([key, item]) => ({ key, label: item.label })));
      setQmsLinked(!!integrations.aiventra_qms?.features?.change_control_link);
    }).catch(() => {});
  }, []);
  // Who may sign which level: Settings → Approval Flow (API Master).
  const apiRights = useApprovalRights('api_master.approval');
  const canDecide = (api) => (
    (api.status === 'Pending Product Approval' && apiRights.canEarlier) ||
    (api.status === 'Pending QA Approval' && apiRights.canFinal)
  );
  const isLocked = (api) => LOCKED_STATUSES.includes(api?.status);

  const loadApis = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiMasterAPI.getAll({ limit: 2000, sortBy: 'api_name', sortOrder: 'asc' });
      setApis(response?.data?.data?.rows || []);
    } catch (error) {
      toast.error('Failed to load API Master data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadApis(); }, [loadApis]);

  useEffect(() => {
    apiMasterAPI.getCleanabilityConfig()
      .then((res) => { if (res?.data?.data?.questions?.length) setCleanConfig(res.data.data); })
      .catch(() => { /* keep defaults */ });
  }, []);

  useEffect(() => {
    const loadReportSettings = async () => {
      try {
        const response = await settingsAPI.getAll();
        const settings = response?.data?.data || {};
        setReportSettings((prev) => ({
          ...prev,
          report_client_name: settings.report_client_name || prev.report_client_name,
          report_client_address: settings.report_client_address || prev.report_client_address,
          report_document_name: settings.report_document_name || prev.report_document_name,
          report_document_rev_no: settings.report_document_rev_no || prev.report_document_rev_no,
          report_client_logo: settings.report_client_logo || prev.report_client_logo,
          report_primeclean_logo: settings.report_primeclean_logo || prev.report_primeclean_logo,
          report_footer_tagline: settings.report_footer_tagline || prev.report_footer_tagline,
          report_format_no_api_master: settings.report_format_no_api_master || prev.report_format_no_api_master
        }));
      } catch (error) {
        // Keep defaults
      }
    };
    loadReportSettings();
  }, []);

  // Support deep-link from Product Master: ?api_id=X — jump straight into editing that API
  // ?new=1 (optionally with &code=&name=) — jump straight into the Add API form
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const apiId = params.get('api_id');
    if (apiId && view === 'list') {
      const existing = apis.find((a) => String(a.api_id) === String(apiId));
      if (existing) {
        handleEdit(existing);
        navigate('/api-master', { replace: true });
      }
      return;
    }
    if (params.get('new') === '1' && view === 'list') {
      handleNew();
      const code = params.get('code') || '';
      const name = params.get('name') || '';
      if (code || name) {
        setForm((f) => ({ ...f, api_code: code, api_name: name }));
      }
      navigate('/api-master', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, view, loading, apis]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleNew = () => {
    setForm({ ...EMPTY_FORM, production_line: localStorage.getItem('activeLine') || '' });
    setFiles({});
    setEditingId(null);
    setEditingApi(null);
    setViewOnly(false);
    setView('form');
  };

  const handleEdit = (api, readOnly = false) => {
    if (!readOnly && api.status === 'Approved') {
      setPendingChangeRef(null);
      setChangeRefGate({ api, readOnly });
      return;
    }
    openEditForm(api, readOnly);
  };

  const confirmChangeReference = async (data) => {
    setChangeRefBusy(true);
    try {
      setPendingChangeRef(data);
      openEditForm(changeRefGate.api, changeRefGate.readOnly);
      setChangeRefGate(null);
    } finally {
      setChangeRefBusy(false);
    }
  };

  const openEditForm = (api, readOnly = false) => {
    const initial = {
      api_code: api.api_code || '',
      api_name: api.api_name || '',
      max_daily_dose: api.max_daily_dose ?? '',
      max_daily_dose_unit: api.max_daily_dose_unit || 'mg',
      therapeutic_dose: api.therapeutic_dose ?? '',
      therapeutic_dose_unit: api.therapeutic_dose_unit || 'mg',
      pde_value: api.pde_value ?? '',
      pde_unit: api.pde_unit || 'mg',
      ld50_value: api.ld50_value ?? '',
      ld50_unit: api.ld50_unit || 'mg/kg',
      solubility_classification: api.solubility_classification || '',
      cleanability_rating: api.cleanability_rating || '',
      is_genotoxic: !!api.is_genotoxic,
      is_carcinogenic: !!api.is_carcinogenic,
      is_sensitizer: !!api.is_sensitizer,
      notes: api.notes || '',
      production_line: api.production_line || '',
      dosage_document_existing: api.dosage_document || api.dose_document || '',
      pde_document_existing: api.pde_document || '',
      ld50_document_existing: api.ld50_document || '',
      solubility_document_existing: api.solubility_document || '',
      cleanability_document_existing: api.cleanability_document || '',
      additional_document_existing: api.additional_document || '',
      // Saved scoring-helper record; form.cleanability_assessment (when set) is the unsaved change
      cleanability_assessment_existing: api.cleanability_assessment || null,
    };
    initialFormRef.current = JSON.stringify(initial);
    setForm(initial);
    setFiles({});
    setEditingId(api.api_id);
    setEditingApi(api);
    setViewOnly(readOnly);
    setView('form');
  };

  // Every write is e-signed. requestSign opens the signature dialog; run() gets { esign_password, esign_comment }.
  const requestSign = (config) => {
    setSignForm({ password: '', comment: '', decision: 'approve' });
    setSignRequest(config);
  };

  const confirmSign = async () => {
    if (!signForm.password || !signForm.comment.trim()) return toast.warning('Password and a reason/comment are required for the electronic signature');
    setSignBusy(true);
    try {
      await signRequest.run({ esign_password: signForm.password, esign_comment: signForm.comment.trim(), decision: signForm.decision });
      setSignRequest(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Action failed');
    } finally {
      setSignBusy(false);
    }
  };

  const handleDelete = (api) => requestSign({
    title: `Deactivate ${api.api_code}`,
    description: `Deactivate API "${api.api_name}" (${api.api_code})? It is blocked while an active product still uses it. It will no longer be selectable for new products.`,
    confirmLabel: 'Sign & Deactivate',
    run: async (sig) => {
      const response = await apiMasterAPI.delete(api.api_id, sig);
      toast.success(response?.data?.message || 'API deactivated');
      loadApis();
    },
  });

  const handleRemoveFromLine = (api) => requestSign({
    title: `Remove ${api.api_code} from line`,
    description: `Remove "${api.api_name}" from production line "${api.production_line}"? The API master record is kept.`,
    confirmLabel: 'Sign & Remove',
    run: async (sig) => {
      const response = await apiMasterAPI.removeFromLine(api.api_id, sig);
      toast.success(response?.data?.message || 'Removed from line');
      loadApis();
    },
  });

  const handleDecision = (api) => requestSign({
    title: `${api.status === 'Pending QA Approval' ? 'QA' : 'Product'} approval — ${api.api_code}`,
    description: `Decide on "${api.api_name}". Return / Reject / Edit Required send it back to the initiator with your comment.`,
    decisionOptions: true,
    confirmLabel: 'Sign & Submit Decision',
    run: async (sig) => {
      const response = await apiMasterAPI.approve(api.api_id, { decision: sig.decision, esign_password: sig.esign_password, esign_comment: sig.esign_comment });
      toast.success(response?.data?.message || 'Decision recorded');
      loadApis();
    },
  });

  // ── Bulk import ────────────────────────────────────────────────────────────
  const parseImportRows = (rawRows) => rawRows.map((raw) => {
    const row = {};
    Object.entries(raw).forEach(([header, value]) => {
      const field = IMPORT_HEADER_MAP[String(header).trim().toLowerCase()] || String(header).trim().toLowerCase().replace(/\s+/g, '_');
      row[field] = typeof value === 'string' ? value.trim() : value;
    });
    return row;
  }).filter((row) => Object.values(row).some((v) => String(v ?? '').trim() !== ''));

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      let rawRows;
      if (/\.csv$/i.test(file.name)) {
        rawRows = await new Promise((resolve, reject) => Papa.parse(file, { header: true, skipEmptyLines: true, complete: (r) => resolve(r.data), error: reject }));
      } else if (/\.xlsx?$/i.test(file.name)) {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        const sheet = wb.SheetNames.find((n) => n.trim().toLowerCase() === 'api details') || wb.SheetNames[0];
        const worksheet = wb.Sheets[sheet];
        const grid = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', blankrows: false });
        const headerIndex = grid.findIndex((cells) => cells.some((cell) =>
          String(cell || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_') === 'api_code'
        ));
        if (headerIndex < 0) throw new Error('Could not find the API Code header in this worksheet');
        const headers = grid[headerIndex].map((cell) => String(cell || '').trim());
        rawRows = grid.slice(headerIndex + 1).map((cells) => headers.reduce((row, header, index) => {
          if (header) row[header] = cells[index] ?? '';
          return row;
        }, {}));
      } else {
        return toast.error('Please upload a CSV or Excel file');
      }
      const rows = parseImportRows(rawRows);
      if (!rows.length) return toast.warning('No data rows found in the file');
      setImportPreview({ fileName: file.name, rows });
    } catch (error) {
      toast.error(`Could not read file: ${error?.message || error}`);
    }
  };

  // Fetched rows only carry what the external master holds (usually code + name); the
  // toxicology / solubility / cleanability fields are completed here or in Excel before
  // the normal e-signed import, and every new API still goes through Product → QA approval.
  const fetchApisFrom = async (source) => {
    setFetchingSource(source.key);
    try {
      const res = await integrationsAPI.pull(source.key, 'apis');
      const data = res?.data?.data || {};
      const rows = (data.rows || []).filter((r) => !r._exists).map(({ _exists, ...rest }) => rest);
      if (!rows.length) { toast.info(`No new APIs in ${source.label} (${data.summary?.existing || 0} already exist)`); return; }
      setImportPreview({ fileName: `Fetched from ${source.label}`, rows, external: true });
      if (data.summary?.existing) toast.info(`${data.summary.existing} API code(s) already exist and were left out`);
    } catch (error) {
      toast.error(error?.response?.data?.message || `Could not fetch APIs from ${source.label}`);
    } finally {
      setFetchingSource('');
    }
  };

  const downloadPreviewForCompletion = () => {
    const headers = Object.keys(IMPORT_TEMPLATE_ROW);
    const sheetRows = importPreview.rows.map((row) => headers.reduce((acc, header) => {
      const field = IMPORT_HEADER_MAP[header.toLowerCase()];
      acc[header] = row[field] ?? '';
      return acc;
    }, {}));
    const ws = XLSX.utils.json_to_sheet(sheetRows, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'API Details');
    XLSX.writeFile(wb, 'api_master_fetched_to_complete.xlsx');
  };

  const downloadImportTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([IMPORT_TEMPLATE_ROW]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'API Details');
    XLSX.writeFile(wb, 'api_master_import_template.xlsx');
  };

  const confirmImport = () => requestSign({
    title: `Import ${importPreview.rows.length} API record(s)`,
    description: 'Each valid row is created as "Pending Product Approval". Rows with a missing mandatory field or an existing API Code are skipped and reported.',
    confirmLabel: 'Sign & Import',
    run: async (sig) => {
      const response = await apiMasterAPI.importRows({ rows: importPreview.rows, esign_password: sig.esign_password, esign_comment: sig.esign_comment });
      setImportResult(response?.data?.data || null);
      setImportPreview(null);
      loadApis();
    },
  });

  const handleSave = async () => {
    if (!form.api_code.trim() || !form.api_name.trim()) {
      return toast.warning('API Code and API Name are required');
    }
    if (!form.max_daily_dose) return toast.warning('Max Daily Dose is required');
    if (!form.therapeutic_dose) return toast.warning('Therapeutic Dose is required');
    if (!form.solubility_classification) return toast.warning('Solubility classification is mandatory (drives risk ranking scoring)');
    if (!form.cleanability_rating) return toast.warning('Cleanability rating is mandatory (drives risk ranking scoring)');

    if (editingId && initialFormRef.current === JSON.stringify(form) && !Object.values(files).some(Boolean)) {
      return toast.info('No changes were made — nothing to save, so no signature is required.');
    }

    requestSign({
      title: editingId ? `Update ${form.api_code}` : `Create ${form.api_code}`,
      description: editingId
        ? 'Saving resubmits this API for Product approval and re-screens the risk & impact assessments of every product that uses it.'
        : 'The new API is submitted for Product approval, then QA approval.',
      confirmLabel: editingId ? 'Sign & Update' : 'Sign & Save',
      run: async (sig) => {
        setSaving(true);
        try {
          const formData = new FormData();
          Object.entries(form).forEach(([key, value]) => {
            if (key.endsWith('_existing')) return;
            if (key === 'cleanability_assessment') {
              // undefined = unchanged (not sent), null = remove, object = new questionnaire result
              if (value !== undefined) formData.append(key, value ? JSON.stringify(value) : '');
              return;
            }
            formData.append(key, typeof value === 'boolean' ? String(value) : (value ?? ''));
          });
          Object.entries(files).forEach(([key, file]) => {
            if (file) formData.append(key, file, file.name);
          });
          formData.append('esign_password', sig.esign_password);
          formData.append('esign_comment', sig.esign_comment);
          if (editingId && editingApi?.status === 'Approved' && pendingChangeRef) {
            formData.append('change_reference_type', pendingChangeRef.reference_type);
            formData.append('change_reference_number', pendingChangeRef.reference_number);
            formData.append('change_reference_files', JSON.stringify(pendingChangeRef.reference_files || []));
          }

          const response = editingId
            ? await apiMasterAPI.update(editingId, formData)
            : await apiMasterAPI.create(formData);
          toast.success(response?.data?.message || (editingId ? 'API updated' : 'API created'));
          await loadApis();
          setView('list');
          setEditingId(null);
          setEditingApi(null);
          setPendingChangeRef(null);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const filteredApis = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return apis.filter((a) => {
      const matchQ = !q || (a.api_name || '').toLowerCase().includes(q) || (a.api_code || '').toLowerCase().includes(q);
      const matchHazard = !hazardOnly || a.is_genotoxic || a.is_carcinogenic || a.is_sensitizer;
      const status = a.status || 'Approved';
      const matchStatus = statusFilter === 'All'
        || (STATUS_GROUPS[statusFilter] ? STATUS_GROUPS[statusFilter].includes(status) : status === statusFilter);
      const matchRisk = !highRiskOnly || getApiRiskFactor(a).risk_level === 'High';
      return matchQ && matchHazard && matchStatus && matchRisk;
    });
  }, [apis, searchQuery, hazardOnly, statusFilter, highRiskOnly]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, hazardOnly, statusFilter, highRiskOnly]);

  // Summary cards count the whole master list (ignoring the active filters) so the numbers stay stable.
  const summary = useMemo(() => {
    const count = (fn) => apis.filter(fn).length;
    const statusOf = (a) => a.status || 'Approved';
    return {
      total: apis.length,
      approved: count((a) => statusOf(a) === 'Approved'),
      pending: count((a) => STATUS_GROUPS[ANY_PENDING].includes(statusOf(a))),
      attention: count((a) => STATUS_GROUPS[NEEDS_ATTENTION].includes(statusOf(a))),
      hazardous: count((a) => a.is_genotoxic || a.is_carcinogenic || a.is_sensitizer),
      highRisk: count((a) => getApiRiskFactor(a).risk_level === 'High'),
    };
  }, [apis]);

  const clearFilters = () => { setStatusFilter('All'); setHazardOnly(false); setHighRiskOnly(false); };
  // Clicking a card selects its filter exclusively; clicking the active card clears it.
  const toggleCard = (kind) => {
    const wasActive = cardActive(kind);
    clearFilters();
    if (wasActive) return;
    if (kind === 'approved') setStatusFilter('Approved');
    if (kind === 'pending') setStatusFilter(ANY_PENDING);
    if (kind === 'attention') setStatusFilter(NEEDS_ATTENTION);
    if (kind === 'hazardous') setHazardOnly(true);
    if (kind === 'highRisk') setHighRiskOnly(true);
  };
  const cardActive = (kind) => ({
    total: statusFilter === 'All' && !hazardOnly && !highRiskOnly,
    approved: statusFilter === 'Approved' && !hazardOnly && !highRiskOnly,
    pending: statusFilter === ANY_PENDING && !hazardOnly && !highRiskOnly,
    attention: statusFilter === NEEDS_ATTENTION && !hazardOnly && !highRiskOnly,
    hazardous: hazardOnly && statusFilter === 'All' && !highRiskOnly,
    highRisk: highRiskOnly && statusFilter === 'All' && !hazardOnly,
  }[kind]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedApis = filteredApis.slice(indexOfFirstItem, indexOfLastItem);

  const getExportContextDisplay = (value, fallback) => {
    const normalized = String(value || '').trim();
    if (!normalized) return fallback;
    if (normalized.toLowerCase() === 'all') return fallback;
    return normalized;
  };

  const getExportMetadata = (recordCount) => {
    let savedUser = null;
    try {
      const userRaw = localStorage.getItem('user');
      savedUser = userRaw ? JSON.parse(userRaw) : null;
    } catch (error) {
      savedUser = null;
    }

    const exportedAt = new Date();
    const exportedBy = savedUser?.username || savedUser?.full_name || savedUser?.name || savedUser?.employee_id || 'Unknown User';
    const exportedByRole = savedUser?.role || 'Unknown Role';

    return {
      reportId: `API-EXP-${exportedAt.getTime()}`,
      exportedAt,
      exportedAtDisplay: exportedAt.toLocaleString(),
      exportedAtIso: exportedAt.toISOString(),
      exportedBy,
      exportedByRole,
      systemName: appName,
      systemHost: window?.location?.origin || 'Unknown Origin',
      systemNode: window?.location?.hostname || 'Unknown Host',
      productionLine: getExportContextDisplay(localStorage.getItem('activeLine'), 'All Sections'),
      recordCount
    };
  };

  const getApiExportRows = (sourceApis) => sourceApis.map((a) => ({
    'API Code': a.api_code || '',
    'API Name': a.api_name || '',
    'Production Line': a.production_line || '',
    'Max Daily Dose': a.max_daily_dose ?? '',
    'Max Daily Dose Unit': a.max_daily_dose_unit || '',
    'Therapeutic Dose': a.therapeutic_dose ?? '',
    'Therapeutic Dose Unit': a.therapeutic_dose_unit || '',
    'PDE Value': a.pde_value ?? '',
    'PDE Unit': a.pde_unit || '',
    'LD50 Value': a.ld50_value ?? '',
    'LD50 Unit': a.ld50_unit || '',
    'Solubility Classification': a.solubility_classification || '',
    'Cleanability Rating': a.cleanability_rating || '',
    'Cleanability Basis': a.cleanability_assessment
      ? `Scoring helper ${a.cleanability_assessment.total}/${a.cleanability_assessment.max} (suggested ${a.cleanability_assessment.suggested}${a.cleanability_assessment.overridden ? `; overridden: ${a.cleanability_assessment.override_reason}` : ''})`
      : '',
    'Genotoxic': a.is_genotoxic ? 'Yes' : 'No',
    'Carcinogenic': a.is_carcinogenic ? 'Yes' : 'No',
    'Sensitizer': a.is_sensitizer ? 'Yes' : 'No',
    'Notes': a.notes || '',
    'Status': a.is_active === false ? 'Inactive' : (a.status || 'Approved'),
  }));

  const drawPdfGridTable = (doc, {
    headers, rows, startY, topMargin, bottomMargin, leftMargin, rightMargin,
    headerTextColor, bodyTextColor, alternateRowColor, fontSize, colWidthWeights, drawHeaderFooter
  }) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const tableWidth = pageWidth - leftMargin - rightMargin;
    const colCount = headers.length;
    const normalizedWeights = Array.isArray(colWidthWeights) && colWidthWeights.length === colCount
      ? colWidthWeights.map((weight) => {
          const parsed = Number(weight);
          return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
        })
      : Array.from({ length: colCount }, () => 1);
    const totalWeight = normalizedWeights.reduce((sum, weight) => sum + weight, 0) || colCount;
    const colWidths = normalizedWeights.map((weight) => (tableWidth * weight) / totalWeight);
    const cellPadding = 3;
    const lineHeight = Math.max(8, fontSize + 2);
    const minRowHeight = Math.max(14, lineHeight + 2);
    const headerFontSize = Math.max(fontSize + 0.6, 6.4);
    const headerLineHeight = Math.max(8, headerFontSize + 1.8);
    const originalFontSize = typeof doc.getFontSize === 'function' ? doc.getFontSize() : fontSize;
    doc.setFontSize(headerFontSize);
    const wrappedHeaderCells = headers.map((header, index) =>
      doc
        .splitTextToSize(String(header || '').trim(), Math.max(colWidths[index] - (cellPadding * 2), 10))
        .map((line) => String(line || '').trim())
        .filter(Boolean)
    );
    doc.setFontSize(originalFontSize);
    const maxHeaderLines = wrappedHeaderCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
    const headerHeight = Math.max(minRowHeight + 4, (maxHeaderLines * headerLineHeight) + (cellPadding * 2) + 2);
    let y = startY;

    const drawHeaderRow = () => {
      doc.setFillColor(255, 255, 255);
      doc.rect(leftMargin, y, tableWidth, headerHeight, 'F');
      doc.setDrawColor(180, 192, 208);
      doc.setTextColor(...headerTextColor);
      doc.setFontSize(headerFontSize);
      doc.setFont('helvetica', 'bold');
      let x = leftMargin;
      headers.forEach((_, index) => {
        doc.rect(x, y, colWidths[index], headerHeight, 'S');
        const lineArray = Array.isArray(wrappedHeaderCells[index]) && wrappedHeaderCells[index].length > 0
          ? wrappedHeaderCells[index]
          : ['-'];
        lineArray.forEach((line, lineIndex) => {
          const textY = y + cellPadding + headerFontSize + (lineIndex * headerLineHeight);
          if (textY <= y + headerHeight - cellPadding) {
            doc.text(line, x + cellPadding, textY);
          }
        });
        x += colWidths[index];
      });
      doc.setFont('helvetica', 'normal');
      y += headerHeight;
    };

    const addNewPageWithHeader = () => {
      doc.addPage();
      if (typeof drawHeaderFooter === 'function') drawHeaderFooter();
      y = topMargin;
      drawHeaderRow();
    };

    if (y + headerHeight > pageHeight - bottomMargin) {
      addNewPageWithHeader();
    } else {
      drawHeaderRow();
    }

    rows.forEach((row, rowIndex) => {
      const wrappedCells = row.map((cell, index) => {
        const cellText = String(cell ?? '-');
        return doc.splitTextToSize(cellText, colWidths[index] - (cellPadding * 2));
      });
      const maxLines = wrappedCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
      const rowHeight = Math.max(minRowHeight, (maxLines * lineHeight) + (cellPadding * 2));

      if (y + rowHeight > pageHeight - bottomMargin) {
        addNewPageWithHeader();
      }

      if (rowIndex % 2 === 1) {
        doc.setFillColor(...alternateRowColor);
        doc.rect(leftMargin, y, tableWidth, rowHeight, 'F');
      }

      doc.setTextColor(...bodyTextColor);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);

      let x = leftMargin;
      wrappedCells.forEach((lines, index) => {
        doc.setDrawColor(203, 213, 225);
        doc.rect(x, y, colWidths[index], rowHeight);

        const lineArray = Array.isArray(lines) && lines.length > 0 ? lines : ['-'];
        lineArray.forEach((line, lineIndex) => {
          const textY = y + cellPadding + fontSize + (lineIndex * lineHeight);
          if (textY <= y + rowHeight - cellPadding) {
            doc.text(line, x + cellPadding, textY);
          }
        });
        x += colWidths[index];
      });

      y += rowHeight;
    });
  };

  const exportApisToExcel = () => {
    if (filteredApis.length === 0) {
      toast.info('No APIs available to export');
      return;
    }

    const rows = getApiExportRows(filteredApis);
    const metadata = getExportMetadata(filteredApis.length);
    const metadataRows = [
      [`${appName} API Master Export`],
      ['21 CFR Part 11 Compliance Context'],
      [],
      ['Report ID', metadata.reportId],
      ['Report Name', 'API Master'],
      ['Section', metadata.productionLine],
      ['Exported At', metadata.exportedAtDisplay],
      ['Exported At (ISO)', metadata.exportedAtIso],
      ['Exported By', metadata.exportedBy],
      ['Exported By Role', metadata.exportedByRole],
      ['System Name', metadata.systemName],
      ['System Node', metadata.systemNode],
      ['System Origin', metadata.systemHost],
      ['API Count', metadata.recordCount],
      ['Note', 'Electronic record export with trace metadata for audit review.']
    ];

    const ws = XLSX.utils.aoa_to_sheet(metadataRows);
    const tableStartRow = metadataRows.length + 2;
    XLSX.utils.sheet_add_json(ws, rows, { origin: `A${tableStartRow}`, skipHeader: false });
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: tableStartRow - 1, c: 0 },
        e: { r: tableStartRow + rows.length - 1, c: Object.keys(rows[0] || {}).length - 1 }
      })
    };
    ws['!cols'] = Object.keys(rows[0] || {}).map((key) => ({ wch: Math.min(Math.max(String(key).length + 6, 14), 40) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'API Details');
    const fileStamp = metadata.exportedAt.toISOString().replace(/[:.]/g, '-');
    XLSX.writeFile(wb, `api_master_${fileStamp}.xlsx`);
    toast.success('Excel exported successfully');
  };

  const exportApisToPDF = async () => {
    // Header/footer from Settings → App Configuration → Reports & Documents.
    const reportSettings = withReportProfile('api_master', 'API Master Report');
    if (filteredApis.length === 0) {
      toast.info('No APIs available to export');
      return;
    }

    try {
      const rows = getApiExportRows(filteredApis);
      const metadata = getExportMetadata(filteredApis.length);
      const [clientLogoData, primeLogoData] = await Promise.all([
        toDataUrlIfPossible(reportSettings.report_client_logo),
        toDataUrlIfPossible(reportSettings.report_primeclean_logo)
      ]);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const headerTop = 16;
      const headerRowSplit = 44;
      const headerBottom = 82;
      const leftLogoWidth = 130;
      const rightLogoWidth = 130;
      const bodyTop = headerBottom + 8;
      const footerTop = pageHeight - 42;

      const truncatePdfHeaderText = (value, maxWidth) => {
        const text = String(value || '').trim();
        if (!text) return '-';
        if (doc.getTextWidth(text) <= maxWidth) return text;
        let truncated = text;
        while (truncated.length > 0 && doc.getTextWidth(`${truncated}...`) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated ? `${truncated}...` : '...';
      };

      const drawHeaderFooter = () => {
        const pageInfo = doc.internal.getCurrentPageInfo ? doc.internal.getCurrentPageInfo() : null;
        const pageNo = pageInfo?.pageNumber || doc.internal.getNumberOfPages();
        const totalPages = doc.internal.getNumberOfPages();

        doc.setDrawColor(80, 80, 80);
        doc.rect(20, headerTop, pageWidth - 40, headerBottom - headerTop);
        doc.line(20 + leftLogoWidth, headerTop, 20 + leftLogoWidth, headerRowSplit);
        doc.line(pageWidth - 20 - rightLogoWidth, headerTop, pageWidth - 20 - rightLogoWidth, headerRowSplit);
        doc.line(20, headerRowSplit, pageWidth - 20, headerRowSplit);

        if (clientLogoData) {
          doc.addImage(clientLogoData, 'PNG', 26, headerTop + 7, leftLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text('Client Logo', 26, headerTop + 19);
        }

        if (primeLogoData) {
          doc.addImage(primeLogoData, 'PNG', pageWidth - 20 - rightLogoWidth + 6, headerTop + 7, rightLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text(`${appName} Logo`, pageWidth - 20 - rightLogoWidth + 8, headerTop + 19);
        }

        doc.setFontSize(8.6);
        doc.setTextColor(15, 23, 42);
        doc.text(reportSettings.report_client_name || 'Client Name', pageWidth / 2, headerTop + 16, { align: 'center' });
        doc.setFontSize(7.6);
        doc.text(unitReportAddress(reportSettings) || 'Client Address', pageWidth / 2, headerTop + 27, { align: 'center' });

        doc.setFontSize(8.5);
        doc.text(truncatePdfHeaderText(`Document Name: ${reportSettings.report_document_name || 'API Master Report'}`, 220), 26, headerRowSplit + 14);
        doc.text(truncatePdfHeaderText(`Section: ${metadata.productionLine}`, 210), pageWidth / 2, headerRowSplit + 14, { align: 'center' });
        doc.text(`Effective Date: ${metadata.exportedAtDisplay}`, pageWidth - 24, headerRowSplit + 14, { align: 'right' });
        doc.text(truncatePdfHeaderText(`API Count: ${metadata.recordCount}`, 260), 26, headerRowSplit + 26);
        doc.text(truncatePdfHeaderText(`Document & Rev. No.: ${reportSettings.report_document_rev_no || '01'}`, 210), pageWidth / 2, headerRowSplit + 26, { align: 'center' });

        doc.line(20, footerTop, pageWidth - 20, footerTop);
        doc.setFontSize(7.2);
        doc.text(
          `Report ID: ${metadata.reportId}  Generated By: ${metadata.exportedBy} (${metadata.exportedByRole})  System: ${metadata.systemName} | ${metadata.systemNode}`,
          pageWidth / 2, pageHeight - 27, { align: 'center' }
        );
        if (reportSettings.report_format_no_api_master) {
          doc.text(`Format No: ${reportSettings.report_format_no_api_master}`, pageWidth / 2, pageHeight - 19, { align: 'center' });
        }
        doc.setFontSize(8);
        doc.text(`Page ${pageNo} of ${totalPages}`, 24, pageHeight - 12);
        doc.text(`Print: ${metadata.exportedAtDisplay}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
        doc.text(reportSettings.report_footer_tagline || `Data is generated and handled by ${appName}.`, pageWidth - 24, pageHeight - 12, { align: 'right' });
      };

      const pdfHeaders = Object.keys(rows[0] || {});
      const pdfBodyRows = rows.map((row) => pdfHeaders.map((header) => row[header]));

      drawHeaderFooter();
      drawPdfGridTable(doc, {
        headers: pdfHeaders,
        rows: pdfBodyRows,
        startY: bodyTop + 4,
        topMargin: bodyTop,
        bottomMargin: 40,
        leftMargin: 20,
        rightMargin: 20,
        headerTextColor: [0, 0, 0],
        bodyTextColor: [15, 23, 42],
        alternateRowColor: [248, 250, 252],
        fontSize: 5.8,
        colWidthWeights: pdfHeaders.map(() => 1),
        drawHeaderFooter
      });

      const fileStamp = metadata.exportedAt.toISOString().replace(/[:.]/g, '-');
      doc.save(`api_master_${fileStamp}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: 'var(--text-secondary)' }}>
        Loading...
      </div>
    );
  }

  const formReadOnly = !canManage || viewOnly || (!!editingId && isLocked(editingApi));

  // Scoring helper shown in the form: the unsaved change if there is one, else the saved record.
  const currentAssessment = form.cleanability_assessment !== undefined ? form.cleanability_assessment : form.cleanability_assessment_existing;

  const handleCleanabilityChange = (value) => {
    if (currentAssessment && currentAssessment.final_rating !== value) {
      toast.info('Rating changed by hand, so the scoring helper result was removed. Run "Help me rate" again to keep it as the basis.');
      setForm((f) => ({ ...f, cleanability_rating: value, cleanability_assessment: null }));
      return;
    }
    setField('cleanability_rating', value);
  };

  const printCleanabilityAssessment = () => printAssessmentReport({
    reportKey: 'cleanability_assessment',
    appName,
    title: `Cleanability Assessment - ${form.api_code}`,
    documentName: 'Cleanability Assessment',
    build: () => cleanabilityAssessmentReportHTML(form, form.cleanability_assessment_existing, cleanConfig),
  });
  const cleanabilitySubject = {
    heading: 'API',
    rows: [['API Code', form.api_code], ['API Name', form.api_name], ['Solubility Classification', form.solubility_classification]],
    fileName: `Cleanability_Checklist_${form.api_code || 'API'}`,
  };

  const cleanabilityDialog = (
    <CleanabilityAssessmentModal
      isOpen={!!assessmentDialog}
      onClose={() => setAssessmentDialog(null)}
      config={cleanConfig}
      initial={currentAssessment}
      readOnly={!!assessmentDialog?.readOnly}
      apiLabel={[form.api_code, form.api_name].filter(Boolean).join(' - ')}
      subject={cleanabilitySubject}
      onApply={(result) => {
        setForm((f) => ({ ...f, cleanability_rating: result.final_rating, cleanability_assessment: result }));
        setAssessmentDialog(null);
      }}
    />
  );

  // Attachment sits directly under the value it supports (same pattern as Product Master's API section)
  // Doc columns hold a filename, or a JSON array of filenames when Product Master merged uploads in.
  const docFileNames = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch { /* plain filename */ }
    return [value];
  };

  const chipStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%', padding: '2px 4px 2px 8px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--card-bg)', fontSize: 'var(--fs-sm)' };
  const removeBtnStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', padding: 0, border: 'none', borderRadius: '4px', background: 'transparent', color: 'var(--danger-text)', cursor: 'pointer' };

  // Themed picker: Choose file button, then the chosen or saved file with a remove (×) button.
  // Removing a saved file sends <key>_remove=true; the server clears it on save.
  const renderDoc = (key, label) => {
    const existing = form[`${key}_remove`] ? [] : docFileNames(form[`${key}_existing`]);
    const chosen = files[key];
    return (
      <div style={{ marginTop: '8px' }}>
        <label className="form-label" style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minHeight: '30px' }}>
          {!formReadOnly && (
            <label className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', margin: 0, cursor: 'pointer' }}>
              <FaUpload /> {chosen || existing.length ? 'Replace file' : 'Choose file'}
              <input
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => { const file = e.target.files?.[0] || null; e.target.value = ''; setFiles((f) => ({ ...f, [key]: file })); }}
              />
            </label>
          )}
          {chosen ? (
            <span style={chipStyle} title={chosen.name}>
              <FaFileAlt style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chosen.name}</span>
              <span style={{ color: 'var(--info-text)', fontSize: 'var(--fs-xs)' }}>new</span>
              <button type="button" style={removeBtnStyle} title="Remove this file" onClick={() => setFiles((f) => ({ ...f, [key]: null }))}><FaTimes /></button>
            </span>
          ) : existing.length > 0 ? existing.map((name) => (
            <span key={name} style={chipStyle} title={name}>
              <FaFileAlt style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
              <a href={toFileUrl(name)} target="_blank" rel="noopener noreferrer" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--primary-color)' }}>{name}</a>
              {!formReadOnly && (
                <button type="button" style={removeBtnStyle} title="Remove this file from the record (applies when you save)" onClick={() => setField(`${key}_remove`, true)}><FaTimes /></button>
              )}
            </span>
          )) : (
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
              {form[`${key}_remove`] ? 'File will be removed when you save' : 'No file chosen'}
              {form[`${key}_remove`] && !formReadOnly && (
                <a href="#undo" style={{ marginLeft: '8px' }} onClick={(e) => { e.preventDefault(); setForm((f) => { const next = { ...f }; delete next[`${key}_remove`]; return next; }); }}>Undo</a>
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  // ── ADD/EDIT MODAL ──────────────────────────────────────────────────────────
  const apiFormModal = (
    <Modal
      isOpen={view === 'form'}
      onClose={() => { setView('list'); setEditingId(null); setEditingApi(null); }}
      title={<><FaFlask /> {!editingId ? 'New' : formReadOnly ? 'View' : 'Edit'} API Master Record</>}
      size="lg"
      footer={
        <>
          {editingId && (
            <button
              className="btn btn-outline"
              type="button"
              data-module="ai_assistant" onClick={() => ai.run('AI Risk Review — API', () => aiAPI.reviewApi(editingId), 'note')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FaRobot /> AI Risk Review
            </button>
          )}
          <button className="btn btn-outline" onClick={() => { setView('list'); setEditingId(null); setEditingApi(null); }}>{formReadOnly ? 'Back' : 'Cancel'}</button>
          {!formReadOnly && (
            <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaSave /> {saving ? 'Saving...' : (editingId ? 'Update API' : 'Save API')}
            </button>
          )}
        </>
      }
    >
        <div style={{ background: 'var(--info-bg)', border: '1.5px solid var(--info-border)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: 'var(--fs-md)', color: 'var(--info-text)' }}>
          This is master data. Solubility, cleanability, PDE/LD50 and hazard classification entered here feed directly into the cleaning-validation risk ranking assessment auto-generated whenever a product using this API is created.
        </div>

        {!canManage && (
          <div style={{ background: 'var(--warning-bg)', border: '1.5px solid var(--warning-border)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: 'var(--fs-md)', color: 'var(--warning-text)' }}>
            Read-only view. Only QA, Admin, or Super Admin can create or edit API Master records.
          </div>
        )}

        {canManage && editingId && isLocked(editingApi) && (
          <div style={{ background: 'var(--warning-bg)', border: '1.5px solid var(--warning-border)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: 'var(--fs-md)', color: 'var(--warning-text)' }}>
            This API is <strong>{editingApi.status}</strong> and is locked for editing. The approver must return it first.
          </div>
        )}

        {editingApi && editingApi.approval_decision && ['Returned', 'Rejected', 'Edit Required'].includes(editingApi.status) && (
          <div style={{ background: 'var(--danger-bg)', border: '1.5px solid var(--danger-border, var(--border-color))', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: 'var(--fs-md)', color: 'var(--danger-text)' }}>
            <strong>{editingApi.status}</strong> at the {editingApi.approval_decision_stage === 'qa' ? 'QA' : 'Product'} stage: {editingApi.approval_decision_comment || 'no comment'}
            {editingApi.status !== 'Rejected' && ' — update the record and save to resubmit.'}
          </div>
        )}

        <fieldset disabled={formReadOnly} style={{ border: 'none', padding: 0, margin: 0 }}>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 'var(--fs-lg)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaCapsules /> Identification
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
            <div style={{ flex: '0 0 180px' }}>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>API Code *</label>
              <input className="form-control form-control-sm" value={form.api_code} onChange={(e) => setField('api_code', e.target.value)} placeholder="e.g. API-001" disabled={!!editingId} />
              {editingId && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginTop: '4px' }}>API Code cannot be changed after creation</div>}
            </div>
            <div style={{ flex: '1 1 280px', minWidth: 0 }}>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>API Name *</label>
              <input className="form-control form-control-sm" value={form.api_name} onChange={(e) => setField('api_name', e.target.value)} placeholder="Active pharmaceutical ingredient" />
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Dosage</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Max Daily Dose *</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input className="form-control form-control-sm" type="number" step="any" min="0" value={form.max_daily_dose} onChange={(e) => setField('max_daily_dose', e.target.value)} style={{ flex: 2 }} />
                <select className="form-control form-control-sm" value={form.max_daily_dose_unit} onChange={(e) => setField('max_daily_dose_unit', e.target.value)} style={{ flex: 1 }}>
                  {DOSE_UNIT_OPTIONS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Therapeutic Dose *</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input className="form-control form-control-sm" type="number" step="any" min="0" value={form.therapeutic_dose} onChange={(e) => setField('therapeutic_dose', e.target.value)} style={{ flex: 2 }} />
                <select className="form-control form-control-sm" value={form.therapeutic_dose_unit} onChange={(e) => setField('therapeutic_dose_unit', e.target.value)} style={{ flex: 1 }}>
                  {DOSE_UNIT_OPTIONS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div style={{ maxWidth: '440px', marginTop: '6px' }}>{renderDoc('dosage_document', 'Dosage Document')}</div>
        </div>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 'var(--fs-lg)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFlask /> Toxicological Data
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>PDE / HBEL Value</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input className="form-control form-control-sm" type="number" step="any" min="0" value={form.pde_value} onChange={(e) => setField('pde_value', e.target.value)} style={{ flex: 2 }} />
                <select className="form-control form-control-sm" value={form.pde_unit} onChange={(e) => setField('pde_unit', e.target.value)} style={{ flex: 1 }}>
                  {PDE_UNIT_OPTIONS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </div>
              {renderDoc('pde_document', 'PDE Document')}
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>LD50 Value</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input className="form-control form-control-sm" type="number" step="any" min="0" value={form.ld50_value} onChange={(e) => setField('ld50_value', e.target.value)} style={{ flex: 2 }} />
                <select className="form-control form-control-sm" value={form.ld50_unit} onChange={(e) => setField('ld50_unit', e.target.value)} style={{ flex: 1 }}>
                  {LD50_UNIT_OPTIONS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </div>
              {renderDoc('ld50_document', 'LD50 Document')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {[
              { key: 'is_genotoxic', label: 'Genotoxic (+3 risk)' },
              { key: 'is_carcinogenic', label: 'Carcinogenic (+3 risk)' },
              { key: 'is_sensitizer', label: 'Sensitizer / Allergen' },
            ].map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: 'var(--fs-md)', color: form[key] ? 'var(--danger-color)' : 'var(--text-secondary)', fontWeight: form[key] ? 700 : 500 }}>
                <input type="checkbox" checked={form[key]} onChange={(e) => setField(key, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Solubility &amp; Cleanability</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Solubility Classification *</label>
              <select className="form-control form-control-sm" value={form.solubility_classification} onChange={(e) => setField('solubility_classification', e.target.value)}>
                <option value="">— Select —</option>
                {SOLUBILITY_OPTIONS.map((o) => <option key={o}>{o}</option>)}
              </select>
              {renderDoc('solubility_document', 'Solubility Document')}
            </div>
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Cleanability Rating *</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select className="form-control form-control-sm" value={form.cleanability_rating} onChange={(e) => handleCleanabilityChange(e.target.value)} style={{ flex: 1 }}>
                  <option value="">— Select —</option>
                  {CLEANABILITY_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                </select>
                {!formReadOnly && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setAssessmentDialog({ readOnly: false })} title="Answer a few questions about the residue to get a suggested rating" style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                    <FaCalculator /> Help me rate
                  </button>
                )}
              </div>
              {currentAssessment && (
                <div style={{ marginTop: '6px', padding: '6px 10px', borderRadius: '6px', background: 'var(--bg-color)', border: '1px solid var(--border-color)', fontSize: 'var(--fs-sm)' }}>
                  <div>
                    Scoring helper: <strong>{currentAssessment.total}/{currentAssessment.max}</strong> → suggested <strong>{currentAssessment.suggested}</strong>
                    {currentAssessment.final_rating !== currentAssessment.suggested && <span style={{ color: 'var(--warning-text)' }}> · overridden to {currentAssessment.final_rating}</span>}
                    {form.cleanability_assessment !== undefined && <span style={{ color: 'var(--info-text)' }}> · not saved yet</span>}
                  </div>
                  {/* Links, not buttons, so they stay usable in the read-only view (disabled fieldset) */}
                  <div style={{ display: 'flex', gap: '12px', marginTop: '2px' }}>
                    <a href="#view" onClick={(e) => { e.preventDefault(); setAssessmentDialog({ readOnly: true }); }}>View answers</a>
                    {form.cleanability_assessment === undefined && (
                      <>
                        <a href="#print" onClick={(e) => { e.preventDefault(); printCleanabilityAssessment(); }}>Print checklist</a>
                        <a href="#excel" onClick={(e) => { e.preventDefault(); exportCleanabilityChecklistXlsx(cleanabilitySubject, cleanConfig, form.cleanability_assessment_existing, cleanabilitySubject.fileName); }}>Export checklist (Excel)</a>
                      </>
                    )}
                    {!formReadOnly && (
                      <a href="#remove" style={{ color: 'var(--danger-text)' }} onClick={(e) => { e.preventDefault(); setField('cleanability_assessment', null); }}>Remove</a>
                    )}
                  </div>
                </div>
              )}
              {renderDoc('cleanability_document', 'Cleanability Document')}
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 'var(--fs-lg)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFileAlt /> Other Supporting Documents
          </h3>
          <div style={{ maxWidth: '440px' }}>{renderDoc('additional_document', 'Additional Document')}</div>
        </div>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
          <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Notes</label>
          <textarea className="form-control form-control-sm" rows={3} value={form.notes} onChange={(e) => setField('notes', e.target.value)} placeholder="Toxicology reference, source of PDE/LD50, or any other justification..." />
        </div>
        </fieldset>
    </Modal>
  );

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <>
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="page-title">API Master</h2>
          <p className="page-subtitle">Toxicology, solubility, cleanability and hazard classification per API — governs cleaning-validation risk ranking scoring for every product that uses it.</p>
        </div>
        <div className="toolbar-full" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-success" onClick={exportApisToExcel} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaDownload /> Excel
          </button>
          <button className="btn btn-danger" onClick={exportApisToPDF} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaDownload /> PDF
          </button>
          {canManage && (
            <button className="btn btn-outline" onClick={() => importInputRef.current?.click()} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaUpload /> Import
            </button>
          )}
          {canManage && apiSyncSources.map((source) => (
            <button key={source.key} className="btn btn-outline" onClick={() => fetchApisFrom(source)} disabled={!!fetchingSource} title={`Fetch new APIs from ${source.label} into the import preview`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaUpload /> {fetchingSource === source.key ? 'Fetching…' : `Fetch from ${source.label}`}
            </button>
          ))}
          {canManage && (
            <button className="btn btn-primary" onClick={handleNew} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPlus /> New API
            </button>
          )}
        </div>
        <input ref={importInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleImportFile} />
        <div className="toolbar-compact" style={{ gap: '8px' }}>
          {canManage && (
            <button className="btn btn-primary" onClick={handleNew} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPlus /> New API
            </button>
          )}
          <ActionsMenu
            title="More actions"
            items={[
              { label: 'Export Excel', icon: <FaDownload />, onClick: exportApisToExcel },
              { label: 'Export PDF', icon: <FaDownload />, onClick: exportApisToPDF },
              { label: 'Import CSV / Excel', icon: <FaUpload />, onClick: () => importInputRef.current?.click(), show: canManage },
              ...apiSyncSources.map((source) => ({ label: `Fetch from ${source.label}`, icon: <FaUpload />, onClick: () => fetchApisFrom(source), show: canManage })),
            ]}
          />
        </div>
      </div>

      <div className="stat-card-grid">
        <StatCard compact filterHint title="Total APIs" value={summary.total} icon={FaCapsules} color="primary" active={cardActive('total')} onClick={() => toggleCard('total')} hint="Show all APIs" />
        <StatCard compact filterHint title="Approved" value={summary.approved} icon={FaCheck} color="success" active={cardActive('approved')} onClick={() => toggleCard('approved')} hint="Filter: Approved" />
        <StatCard compact filterHint title="Pending Approval" value={summary.pending} icon={FaHourglassHalf} color="info" active={cardActive('pending')} onClick={() => toggleCard('pending')} hint="Filter: waiting for Product or QA approval" />
        <StatCard compact filterHint title="Needs Attention" value={summary.attention} icon={FaExclamationTriangle} color="warning" active={cardActive('attention')} onClick={() => toggleCard('attention')} hint="Filter: Returned / Edit Required / Rejected" />
        <StatCard compact filterHint title="Hazardous" value={summary.hazardous} icon={FaRadiation} color="danger" active={cardActive('hazardous')} onClick={() => toggleCard('hazardous')} hint="Filter: genotoxic / carcinogenic / sensitizer" />
        <StatCard compact filterHint title="High Risk" value={summary.highRisk} icon={FaFire} color="danger" active={cardActive('highRisk')} onClick={() => toggleCard('highRisk')} hint="Filter: High API-level risk factor" />
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} />
          <input className="form-control form-control-sm" style={{ paddingLeft: '30px' }} placeholder="Search API code or name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <select className="form-control form-control-sm" style={{ width: 'auto' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} title="Filter by approval status">
          {[...STATUS_FILTER_OPTIONS, ANY_PENDING, NEEDS_ATTENTION].map((s) => <option key={s} value={s}>{s === 'All' ? 'All statuses' : s}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--fs-md)', cursor: 'pointer', color: hazardOnly ? 'var(--danger-color)' : 'var(--text-secondary)', fontWeight: hazardOnly ? 700 : 500 }}>
          <input type="checkbox" checked={hazardOnly} onChange={(e) => setHazardOnly(e.target.checked)} />
          Hazardous only (genotoxic / carcinogenic / sensitizer)
        </label>
      </div>

      {filteredApis.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          <FaCapsules style={{ fontSize: 'var(--fs-3xl)', marginBottom: '12px', opacity: 0.4 }} />
          <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, marginBottom: '8px' }}>No APIs found</div>
          {canManage && apis.length === 0 && (
            <button className="btn btn-primary" onClick={handleNew} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
              <FaPlus /> Create First API
            </button>
          )}
        </div>
      ) : (
        <FloatingScrollWrapper innerClassName="table-container">
          <table>
            <thead>
              <tr>
                {['API Code', 'API Name', 'Max Daily Dose', 'PDE', 'LD50', 'Solubility', 'Cleanability', 'Risk Factor', 'Hazard Flags', 'Status', 'Actions'].map((h) => (
                  <th
                    key={h}
                    style={h === 'Actions' ? { width: '120px', textAlign: 'center' } : { whiteSpace: 'nowrap' }}
                    title={h === 'Risk Factor' ? 'API-level risk contribution from toxicity, solubility and cleanability only — the full product risk ranking also factors in patient population, batch size and equipment sharing (see Risk Assessment).' : undefined}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedApis.map((api) => (
                <tr key={api.api_id} onDoubleClick={() => handleEdit(api, true)}>
                  <td><strong>{api.api_code}</strong></td>
                  <td>{api.api_name}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{api.max_daily_dose ?? '—'} {api.max_daily_dose_unit}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{api.pde_value ?? '—'} {api.pde_value ? api.pde_unit : ''}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{api.ld50_value ?? '—'} {api.ld50_value ? api.ld50_unit : ''}</td>
                  <td>{api.solubility_classification || '—'}</td>
                  <td>{api.cleanability_rating || '—'}</td>
                  <td>
                    {(() => {
                      const risk = getApiRiskFactor(api);
                      const colors = RISK_LEVEL_COLORS[risk.risk_color] || RISK_LEVEL_COLORS.green;
                      return (
                        <span
                          title="API-level risk factor (toxicity + solubility + cleanability). Full product risk ranking is computed per-product in Risk Assessment."
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-xs)', background: colors.badge, color: colors.text, border: `1px solid ${colors.border}`, whiteSpace: 'nowrap' }}
                        >
                          {risk.risk_level} ({risk.total})
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {api.is_genotoxic && <span style={HAZARD_BADGE_STYLE}>Genotoxic</span>}
                    {api.is_carcinogenic && <span style={HAZARD_BADGE_STYLE}>Carcinogenic</span>}
                    {api.is_sensitizer && <span style={{ ...HAZARD_BADGE_STYLE, background: 'var(--warning-bg)', color: 'var(--warning-text)' }}>Sensitizer</span>}
                    {!api.is_genotoxic && !api.is_carcinogenic && !api.is_sensitizer && <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>—</span>}
                  </td>
                  <td>
                    {(() => {
                      const status = api.status || 'Approved';
                      return (
                        <span
                          title={api.approval_decision_comment ? `${status}: ${api.approval_decision_comment}` : status}
                          style={{ ...(STATUS_STYLES[status] || {}), padding: '3px 9px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-xs)', whiteSpace: 'nowrap' }}
                        >
                          {status}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="action-cell" style={{ width: '120px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      {canDecide(api) && (
                        <button className="btn btn-sm btn-success" onClick={() => handleDecision(api)} title="Approve / Return / Reject">
                          <FaCheck />
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline" onClick={() => handleEdit(api, true)} title="View">
                        <FaEye />
                      </button>
                      <ActionsMenu items={[
                        { label: 'Edit', icon: <FaEdit />, onClick: () => handleEdit(api), show: canManage && !isLocked(api) },
                        { label: 'Version History', icon: <FaHourglassHalf />, onClick: () => setHistoryModal(api) },
                        { label: 'Remove from line', icon: <FaUnlink />, onClick: () => handleRemoveFromLine(api), show: canManage && !!api.production_line },
                        { label: 'Deactivate', icon: <FaTrash />, onClick: () => handleDelete(api), show: canManage, danger: true },
                      ]} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </FloatingScrollWrapper>
      )}

      {filteredApis.length > 0 && (
        <TablePagination total={filteredApis.length} page={currentPage} pageSize={itemsPerPage} onPageChange={setCurrentPage} onPageSizeChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }} itemLabel="APIs" />
      )}
    </div>
    {apiFormModal}
    {cleanabilityDialog}

    <Modal
      isOpen={!!importPreview}
      onClose={() => setImportPreview(null)}
      title={<><FaUpload /> Import APIs</>}
      size="lg"
      footer={
        <>
          {importPreview?.external
            ? <button className="btn btn-outline" onClick={downloadPreviewForCompletion} title="Download these rows to fill the toxicology / solubility / cleanability fields, then import the file"><FaDownload /> Download to complete</button>
            : <button className="btn btn-outline" onClick={downloadImportTemplate}><FaDownload /> Template</button>}
          <button className="btn btn-outline" onClick={() => setImportPreview(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={confirmImport}>Continue to e-signature</button>
        </>
      }
    >
      {importPreview && (
        <div>
          <p style={{ marginTop: 0, fontSize: 'var(--fs-md)' }}>
            {importPreview.external && (
              <span style={{ display: 'block', marginBottom: '6px', color: 'var(--warning-text, #a16207)' }}>
                External masters usually hold only code and name. Rows missing a mandatory field are reported and skipped — use “Download to complete”, fill the missing fields, then Import the file.
              </span>
            )}
            <strong>{importPreview.fileName}</strong> — {importPreview.rows.length} row(s). Mandatory: API Code, API Name, Max Daily Dose, Therapeutic Dose, Solubility Classification, Cleanability Rating. Existing API Codes are skipped. New APIs enter Product → QA approval.
          </p>
          <div style={{ maxHeight: '320px', overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
              <thead>
                <tr style={{ background: 'var(--bg-color)' }}>
                  {['#', 'API Code', 'API Name', 'Max Daily Dose', 'Solubility', 'Cleanability'].map((h) => <th key={h} style={{ padding: '6px 10px', textAlign: 'left' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {importPreview.rows.slice(0, 50).map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '6px 10px' }}>{i + 1}</td>
                    <td style={{ padding: '6px 10px' }}>{r.api_code}</td>
                    <td style={{ padding: '6px 10px' }}>{r.api_name}</td>
                    <td style={{ padding: '6px 10px' }}>{r.max_daily_dose}</td>
                    <td style={{ padding: '6px 10px' }}>{r.solubility_classification}</td>
                    <td style={{ padding: '6px 10px' }}>{r.cleanability_rating}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {importPreview.rows.length > 50 && <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Showing the first 50 of {importPreview.rows.length} rows.</p>}
        </div>
      )}
    </Modal>

    <Modal
      isOpen={!!importResult}
      onClose={() => setImportResult(null)}
      title="Import result"
      size="lg"
      footer={<button className="btn btn-primary" onClick={() => setImportResult(null)}>Close</button>}
    >
      {importResult && (
        <div>
          <p style={{ marginTop: 0, fontSize: 'var(--fs-md)' }}>
            <strong>{importResult.created}</strong> of {importResult.total} row(s) created and submitted for Product approval.
          </p>
          {importResult.results.filter((r) => r.status !== 'created').length > 0 && (
            <ul style={{ fontSize: 'var(--fs-sm)', paddingLeft: '18px' }}>
              {importResult.results.filter((r) => r.status !== 'created').map((r) => (
                <li key={r.row} style={{ color: r.status === 'error' ? 'var(--danger-text)' : 'var(--warning-text)' }}>
                  Row {r.row}{r.api_code ? ` (${r.api_code})` : ''} — {r.status}: {r.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>

    <ChangeReferenceModal
      open={!!changeRefGate}
      qmsLinked={qmsLinked}
      busy={changeRefBusy}
      recordLabel={changeRefGate?.api?.api_name}
      onCancel={() => setChangeRefGate(null)}
      onConfirm={confirmChangeReference}
    />

    <Modal
      isOpen={!!signRequest}
      onClose={() => { if (!signBusy) setSignRequest(null); }}
      title={<><FaCheck /> {signRequest?.title}</>}
      footer={
        <>
          <button className="btn btn-outline" disabled={signBusy} onClick={() => setSignRequest(null)}>Cancel</button>
          <button className="btn btn-primary" disabled={signBusy} onClick={confirmSign}>{signBusy ? 'Signing...' : signRequest?.confirmLabel}</button>
        </>
      }
    >
      {signRequest && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>{signRequest.description}</div>
          {signRequest.decisionOptions && (
            <div>
              <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Decision</label>
              <select className="form-control form-control-sm" value={signForm.decision} onChange={(e) => setSignForm((f) => ({ ...f, decision: e.target.value }))}>
                <option value="approve">Approve</option>
                <option value="return">Return</option>
                <option value="reject">Reject</option>
                <option value="edit_required">Edit Required</option>
              </select>
            </div>
          )}
          <div>
            <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Reason / comment *</label>
            <textarea className="form-control form-control-sm" rows={2} value={signForm.comment} onChange={(e) => setSignForm((f) => ({ ...f, comment: e.target.value }))} />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Password (electronic signature) *</label>
            <input className="form-control form-control-sm" type="password" autoComplete="current-password" value={signForm.password} onChange={(e) => setSignForm((f) => ({ ...f, password: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') confirmSign(); }} />
          </div>
        </div>
      )}
    </Modal>

    <VersionHistoryModal
      isOpen={!!historyModal}
      onClose={() => setHistoryModal(null)}
      title={historyModal ? `Version History — ${historyModal.api_name}` : ''}
      fetcher={() => apiMasterAPI.getRevisions(historyModal.api_id)}
    />

    <AIResultPanel
      isOpen={ai.isOpen}
      onClose={ai.close}
      title={ai.title}
      loading={ai.loading}
      error={ai.error}
      content={ai.content}
      docType="note"
      exportFileName="api-ai-review.pdf"
    />
    </>
  );
};

export default ApiMaster;
